import { api } from '../libs/api'
import { getRequestConfig } from '../utils/auth'

export interface SubscriptionProduct {
  productType: 'agency_plan' | 'agent_seat'
  productId: string
  priceId: string
  unitQty: number
  unitPrice: number
  createdAt: string
  updatedAt: string
}

export interface AuditEvent {
  eventType: 'agent_invited' | 'agent_join_requested' | 'invite_accepted' | 'invite_rejected' | 'invite_revoked' | 'trial_started' | 'agent_licensed' | 'agent_removed' | 'agent_synced' | 'agency_synced'
  agentId?: string
  agentEmail?: string
  agentName?: string
  performedBy?: string
  performedByEmail?: string
  performedByName?: string
  metadata?: { [key: string]: any }
  description: string
  timestamp: string
}

export interface Agency {
  id: string
  name: string
  agencyCode: string
  email: string
  phone?: string
  address?: string
  website?: string
  description?: string
  createdBy: string
  ownedBy: string
  billing?: {
    subscriptions?: Array<{
      stripePriceId?: string
      stripeCustomerId?: string
      stripeSubscriptionId?: string
      stripeProducts?: SubscriptionProduct[]
      stripeSubscriptionStatus?: boolean // Default to true, webhook will set to false when needed
      createdAt?: string
      updatedAt?: string
    }>
    billingInfo?: {
      companyName?: string
      billingEmail?: string
      address?: string
      city?: string
      postalCode?: string
      country?: string
      vatNumber?: string
    }
  }
  billingInfo?: {
    companyName?: string
    billingEmail?: string
    address?: string
    city?: string
    postalCode?: string
    country?: string
    vatNumber?: string
  }
  settings?: {
    defaultAgencySeatCost?: number
    defaultAgencyBasePlanCost?: number
    defaultTrialPeriod?: number
  }
  auditInfo?: {
    events: AuditEvent[]
  }
  agents?: Array<{
    userId: string
    role: 'agent' | 'admin'
    invite: {
      status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'requested'
      createdAt: string
      updatedAt: string
    }
    paymentResponsibility: 'agent' | 'agency' | 'none'
    license: {
      status: 'none' | 'licensed' | 'trial'
      createdAt: string
      updatedAt: string
    }
  }>
  isActive?: boolean
  createdAt: string
  updatedAt: string
}

export interface Agent {
  id: {
    _id: string
    authId: string
    email: string
    id: string
  }
  userId: {
    _id: string
    authId: string
    email: string
    id: string
  }
  role: 'agent' | 'admin'
  invite: {
    status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'requested'
    createdAt: string
    updatedAt: string
  }
  paymentResponsibility: 'agent' | 'agency' | 'none'
  license: {
    status: 'none' | 'licensed' | 'trial'
    createdAt: string
    updatedAt: string
  }
  user: {
    _id: string
    authId: string
    email: string
    id: string
    firstName?: string
    lastName?: string
  }
}

export interface AgencyCreateData {
  name: string
  agencyCode: string
  email: string
  phone?: string
  address?: string
  website?: string
  description?: string
  billing?: {
    subscriptions?: Array<{
      stripePriceId?: string
      stripeCustomerId?: string
      stripeSubscriptionId?: string
      stripeSubscriptionStatus?: boolean // Default to true, webhook will set to false when needed
      createdAt?: string
      updatedAt?: string
    }>
    billingInfo?: {
      companyName?: string
      billingEmail?: string
      address?: string
      city?: string
      postalCode?: string
      country?: string
      vatNumber?: string
    }
  }
}

export interface AgencyUpdateData {
  name?: string
  agencyCode?: string
  email?: string
  phone?: string
  address?: string
  website?: string
  description?: string
  billing?: {
    subscriptions?: Array<{
      stripePriceId?: string
      stripeCustomerId?: string
      stripeSubscriptionId?: string
      stripeProducts?: SubscriptionProduct[]
      stripeSubscriptionStatus?: boolean // Default to true, webhook will set to false when needed
      createdAt?: string
      updatedAt?: string
    }>
    billingInfo?: {
      companyName?: string
      billingEmail?: string
      address?: string
      city?: string
      postalCode?: string
      country?: string
      vatNumber?: string
    }
  }
  settings?: {
    defaultAgencySeatCost?: number
    defaultAgencyBasePlanCost?: number
    defaultTrialPeriod?: number
  }
  isActive?: boolean
}

export interface AgencyCreateComprehensiveData {
  name: string
  agencyCode: string
  phone?: string
  address?: string
  website?: string
  description?: string
  billing?: {
    subscriptions?: Array<{
      stripePriceId?: string
      stripeCustomerId?: string
      stripeSubscriptionId?: string
      stripeProducts?: SubscriptionProduct[]
      stripeSubscriptionStatus?: boolean // Default to true, webhook will set to false when needed
      createdAt?: string
      updatedAt?: string
    }>
    billingInfo?: {
      companyName?: string
      billingEmail?: string
      address?: string
      city?: string
      postalCode?: string
      country?: string
      vatNumber?: string
    }
  }
}

export interface AgencyListResponse {
  success: boolean
  data: Agency[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface AgencyResponse {
  success: boolean
  data: Agency
  message?: string
}

export interface AgencyStatsResponse {
  success: boolean
  data: {
    agency: Agency
    stats: {
      totalAgents: number
      activeAgents: number
      pendingAgents: number
      licensedAgents: number
      unlicensedAgents: number
      agencyPaidLicenses: number
      selfPaidLicenses: number
      recentLogins: number
    }
  }
}

export interface AgencyListParams {
  page?: number
  limit?: number
  search?: string
}

export interface InviteAgentRequest {
  email: string
  role?: 'agent' | 'admin'
  paymentResponsibility?: 'agent' | 'agency' | 'none'
}

export interface BulkAddAgentsRequest {
  emails: string[]
  role?: 'agent' | 'admin'
  paymentResponsibility?: 'agent' | 'agency' | 'none'
}

export interface BulkAddAgentsResponse {
  success: boolean
  data: {
    added: string[]
    alreadyExist: string[]
    notFound: string[]
  }
  message: string
}

export interface BulkInviteAgentsRequest {
  emails: string[]
  role?: 'agent' | 'admin'
  paymentResponsibility?: 'agent' | 'agency' | 'none'
}

export interface BulkInviteAgentsResponse {
  success: boolean
  data: {
    invited: string[]
    alreadyExist: string[]
  }
  message: string
}

export interface InviteAgentResponse {
  success: boolean
  data: {
    _id: string
    userId: string
    agencyId: string
    role: 'agent' | 'admin'
    status: 'pending'
    paymentResponsibility: 'agent' | 'agency'
    license: {
      status: 'none' | 'licensed' | 'trial'
      createdAt: string
      updatedAt: string
    }
    invitedBy: string
    invitedAt: string
    user: any
    agency: any
  }
  message: string
}

export interface UserInvite {
  agencyId: string
  agencyName: string
  agencyCode: string
  role: 'agent' | 'admin'
  paymentResponsibility: 'agent' | 'agency' | 'none'
  inviteStatus: 'pending' | 'accepted' | 'declined' | 'revoked' | 'requested'
  invitedAt: string
  agencyCreatedBy: string
}

export interface UserInvitesResponse {
  success: boolean
  data: UserInvite[]
  message?: string
}

class AgencyService {
  private readonly baseUrl = '/agencies'

  async getAllAgencies(params: AgencyListParams = {}): Promise<{ agencies: Agency[]; pagination: AgencyListResponse['pagination'] }> {
    try {
      const searchParams = new URLSearchParams()

      if (params.page) searchParams.append('page', params.page.toString())
      if (params.limit) searchParams.append('limit', params.limit.toString())
      if (params.search) searchParams.append('search', params.search)

      const url = `${this.baseUrl}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      const response = await api.get(url, getRequestConfig()) as AgencyListResponse

      if (!response.success) {
        throw new Error('Failed to fetch agencies')
      }

      return {
        agencies: response.data,
        pagination: response.pagination
      }
    } catch (error) {
      console.error('Error fetching agencies:', error)
      throw error
    }
  }

  async getAgencyById(id: string): Promise<Agency> {
    try {
      const response = await api.get(`${this.baseUrl}/${id}`, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to fetch agency')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching agency:', error)
      throw error
    }
  }

  async getAgents(id: string, params: { page?: number; limit?: number; status?: string; role?: string; search?: string; paymentResponsibility?: string; license?: string; sortBy?: string; sortDirection?: string } = {}): Promise<{ agents: Agent[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    try {
      const searchParams = new URLSearchParams()

      if (params.page) searchParams.append('page', params.page.toString())
      if (params.limit) searchParams.append('limit', params.limit.toString())
      if (params.status) searchParams.append('status', params.status)
      if (params.role) searchParams.append('role', params.role)
      if (params.search) searchParams.append('search', params.search)
      if (params.paymentResponsibility) searchParams.append('paymentResponsibility', params.paymentResponsibility)
      if (params.license) searchParams.append('license', params.license)
      if (params.sortBy) searchParams.append('sortBy', params.sortBy)
      if (params.sortDirection) searchParams.append('sortDirection', params.sortDirection)

      const url = `${this.baseUrl}/${id}/agents${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
      const response = await api.get(url, getRequestConfig()) as { success: boolean; data: Agent[]; pagination: { page: number; limit: number; total: number; pages: number } }

      if (!response.success) {
        throw new Error('Failed to fetch agency agents')
      }

      return {
        agents: response.data,
        pagination: response.pagination
      }
    } catch (error) {
      console.error('Error fetching agency agents:', error)
      throw error
    }
  }

  async getAgencyStats(id: string): Promise<AgencyStatsResponse['data']> {
    try {
      const response = await api.get(`${this.baseUrl}/${id}/stats`, getRequestConfig()) as AgencyStatsResponse

      if (!response.success) {
        throw new Error('Failed to fetch agency statistics')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching agency statistics:', error)
      throw error
    }
  }

  async createAgency(agencyData: AgencyCreateData): Promise<Agency> {
    try {
      const response = await api.post(this.baseUrl, agencyData, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to create agency')
      }

      return response.data
    } catch (error) {
      console.error('Error creating agency:', error)
      throw error
    }
  }

  async createAgencyComprehensive(agencyData: AgencyCreateComprehensiveData): Promise<Agency> {
    try {
      const response = await api.post(this.baseUrl, agencyData) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to create agency')
      }

      return response.data
    } catch (error) {
      console.error('Error creating comprehensive agency:', error)
      throw error
    }
  }

  async createBasicAgency(agencyData: {
    name: string
    agencyCode: string
    phone?: string
    address?: string
    website?: string
    description?: string
  }): Promise<Agency> {
    try {
      const response = await api.post(this.baseUrl, agencyData, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to create basic agency')
      }

      return response.data
    } catch (error) {
      console.error('Error creating basic agency:', error)
      throw error
    }
  }

  async updateAgency(id: string, agencyData: AgencyUpdateData): Promise<Agency> {
    try {
      const response = await api.put(`${this.baseUrl}/${id}`, agencyData, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to update agency')
      }

      return response.data
    } catch (error) {
      console.error('Error updating agency:', error)
      throw error
    }
  }

  async deleteAgency(id: string): Promise<void> {
    try {
      const response = await api.delete(`${this.baseUrl}/${id}`)

      if (!response.success) {
        throw new Error('Failed to delete agency')
      }
    } catch (error) {
      console.error('Error deleting agency:', error)
      throw error
    }
  }

  async checkAgencyCodeAvailability(agencyCode: string, agencyId?: string): Promise<{ available: boolean; agencyCode: string }> {
    try {
      const url = agencyId
        ? `${this.baseUrl}/check-code/${agencyCode}?agencyId=${agencyId}`
        : `${this.baseUrl}/check-code/${agencyCode}`;

      const response = await api.get(url, getRequestConfig()) as { success: boolean; data: { available: boolean; agencyCode: string } }

      if (!response.success) {
        throw new Error('Failed to check agency code availability')
      }

      return response.data
    } catch (error) {
      console.error('Error checking agency code availability:', error)
      throw error
    }
  }

  async getAgencyByCode(agencyCode: string): Promise<Agency> {
    try {
      const response = await api.get(`${this.baseUrl}/by-code/${agencyCode}`, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to get agency by code')
      }

      return response.data
    } catch (error) {
      console.error('Error getting agency by code:', error)
      throw error
    }
  }

  async inviteAgent(agencyId: string, inviteData: InviteAgentRequest): Promise<InviteAgentResponse['data']> {
    try {
      const response = await api.post(`${this.baseUrl}/${agencyId}/agents/invite`, inviteData, getRequestConfig()) as InviteAgentResponse

      if (!response.success) {
        throw new Error('Failed to invite agent')
      }

      return response.data
    } catch (error) {
      console.error('Error inviting agent:', error)
      throw error
    }
  }

  async addAgent(agencyId: string, addData: InviteAgentRequest): Promise<InviteAgentResponse['data']> {
    try {
      const response = await api.post(`${this.baseUrl}/${agencyId}/agents/add`, addData, getRequestConfig()) as InviteAgentResponse

      if (!response.success) {
        throw new Error('Failed to add agent')
      }

      return response.data
    } catch (error) {
      console.error('Error adding agent:', error)
      throw error
    }
  }

  async bulkAddAgents(agencyId: string, data: BulkAddAgentsRequest): Promise<BulkAddAgentsResponse['data']> {
    try {
      const response = await api.post(`${this.baseUrl}/${agencyId}/agents/bulk-add`, data, getRequestConfig()) as BulkAddAgentsResponse

      if (!response.success) {
        throw new Error('Failed to bulk add agents')
      }

      return response.data
    } catch (error) {
      console.error('Error bulk adding agents:', error)
      throw error
    }
  }

  async bulkInviteAgents(agencyId: string, data: BulkInviteAgentsRequest): Promise<BulkInviteAgentsResponse['data']> {
    try {
      const response = await api.post(`${this.baseUrl}/${agencyId}/agents/bulk-invite`, data, getRequestConfig()) as BulkInviteAgentsResponse

      if (!response.success) {
        throw new Error('Failed to bulk invite agents')
      }

      return response.data
    } catch (error) {
      console.error('Error bulk inviting agents:', error)
      throw error
    }
  }

  async validateEmail(email: string): Promise<{ exists: boolean; hasAccount: boolean; authId?: string; name?: string; error?: string }> {
    try {
      const response = await api.get(`${this.baseUrl}/validate-email/${encodeURIComponent(email)}`, getRequestConfig()) as {
        success: boolean
        exists: boolean
        hasAccount: boolean
        authId?: string
        name?: string
        error?: string
      }

      return {
        exists: response.exists,
        hasAccount: response.hasAccount,
        authId: response.authId,
        name: response.name,
        error: response.error
      }
    } catch (error: any) {
      console.error('Error validating email:', error)
      return {
        exists: false,
        hasAccount: false,
        error: error.message || 'Failed to validate email'
      }
    }
  }

  async getUserInvites(): Promise<UserInvite[]> {
    try {
      const response = await api.get('/agencies/invites', getRequestConfig()) as UserInvitesResponse

      if (!response.success) {
        throw new Error('Failed to fetch user invites')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching user invites:', error)
      throw error
    }
  }

  async revokeAgentAccess(agencyId: string, agentId: string): Promise<void> {
    try {
      const response = await api.put(`/agencies/${agencyId}/agents/${agentId}/revoke`)

      if (!response.success) {
        throw new Error('Failed to revoke agent access')
      }
    } catch (error) {
      console.error('Error revoking agent access:', error)
      throw error
    }
  }

  async deleteAgent(agencyId: string, agentId: string): Promise<void> {
    try {
      const response = await api.delete(`/agencies/${agencyId}/agents/${agentId}`, getRequestConfig())

      if (!response.success) {
        throw new Error('Failed to delete agent')
      }
    } catch (error) {
      console.error('Error deleting agent:', error)
      throw error
    }
  }

  async syncAgencyAgents(agencyId: string): Promise<{ syncedCount: number; userAuthIds: string[] }> {
    try {
      const response = await api.post(`/agencies/${agencyId}/sync-agents`, {}, getRequestConfig()) as {
        success: boolean
        message?: string
        data?: { syncedCount: number; userAuthIds: string[] }
        error?: string
      }

      if (!response.success) {
        throw new Error(response.error || 'Failed to sync agents')
      }

      return response.data || { syncedCount: 0, userAuthIds: [] }
    } catch (error) {
      console.error('Error syncing agency agents:', error)
      throw error
    }
  }

  async acceptInvite(agencyId: string, userId: string): Promise<void> {
    try {
      const response = await api.put(`/agencies/${agencyId}/agents/${userId}/status`, {
        status: 'accepted'
      })

      if (!response.success) {
        throw new Error('Failed to accept invitation')
      }
    } catch (error) {
      console.error('Error accepting invitation:', error)
      throw error
    }
  }

  async rejectInvite(agencyId: string, userId: string): Promise<void> {
    try {
      const response = await api.put(`/agencies/${agencyId}/agents/${userId}/status`, {
        status: 'declined'
      })

      if (!response.success) {
        throw new Error('Failed to reject invitation')
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error)
      throw error
    }
  }

  async updateAgentStatus(agencyId: string, userId: string, status: 'accepted' | 'declined', role?: 'agent' | 'admin', paymentResponsibility?: 'agent' | 'agency' | 'none'): Promise<void> {
    try {
      const body: any = { status };
      if (role) body.role = role;
      if (paymentResponsibility) body.paymentResponsibility = paymentResponsibility;
      const response = await api.put(`/agencies/${agencyId}/agents/${userId}/status`, body);
      if (!response.success) {
        throw new Error(`Failed to update agent status to ${status}`);
      }
    } catch (error) {
      console.error('Error updating agent status:', error);
      throw error;
    }
  }

  async createAgencyWithAdmin(agencyData: {
    name: string
    agencyCode: string
    phone?: string
    website?: string
    description?: string
    agencyAdminEmail: string
    agencyBasePlanCost: number
    agencySeatCost: number
  }): Promise<Agency> {
    try {
      const response = await api.post(`${this.baseUrl}/with-admin`, agencyData, getRequestConfig()) as AgencyResponse

      if (!response.success) {
        throw new Error('Failed to create agency with admin')
      }

      return response.data
    } catch (error) {
      console.error('Error creating agency with admin:', error)
      throw error
    }
  }

  async requestToJoinAgency(agencyCode: string, requestData: {
    role?: 'agent' | 'admin'
    paymentResponsibility?: 'agent' | 'agency' | 'none'
  }): Promise<{
    agencyId: string
    agencyName: string
    agencyCode: string
    role: 'agent' | 'admin'
    paymentResponsibility: 'agent' | 'agency' | 'none'
    status: 'requested'
  }> {
    try {
      const response = await api.post(`/agencies/${agencyCode}/join-request`, requestData) as {
        success: boolean
        data: {
          agencyId: string
          agencyName: string
          agencyCode: string
          role: 'agent' | 'admin'
          paymentResponsibility: 'agent' | 'agency' | 'none'
          status: 'requested'
        }
        message: string
      }

      if (!response.success) {
        throw new Error('Failed to send join request')
      }

      return response.data
    } catch (error) {
      console.error('Error sending join request:', error)
      throw error
    }
  }

  async getJoinRequests(agencyId: string): Promise<Agent[]> {
    try {
      const response = await api.get(`/agencies/${agencyId}/join-requests`, getRequestConfig()) as { success: boolean; data: Agent[] }

      if (!response.success) {
        throw new Error('Failed to fetch join requests')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching join requests:', error)
      throw error
    }
  }
}

// Create and export the service instance
export const agencyService = new AgencyService()
export default agencyService 